import { useState } from "react";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import { ChapterHeading, FadeUp, WHATSAPP_URL, PHONE_DISPLAY, EMAIL, FloralCorner } from "./shared";

const ContactSection = () => {
    const [form, setForm] = useState({ nome: "", telefone: "", mensagem: "" });

    const handleSubmit = (e) => {
        e.preventDefault();
        const text = `Olá, Maricleia! Meu nome é ${form.nome}.\nTelefone: ${form.telefone}\n\n${form.mensagem}`;
        window.open(`${WHATSAPP_URL}?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
    };

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const inputCls =
        "w-full rounded-2xl border-2 border-lilac bg-white px-5 py-4 text-base text-plum placeholder:text-ink/50 outline-none transition-[border-color,box-shadow] duration-300 focus:border-plum focus:shadow-lg focus:shadow-plum/10 min-h-[52px]";

    return (
        <section id="contato" className="relative overflow-hidden py-20 sm:py-28" data-testid="contact-section">
            <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-full bg-gradient-to-b from-cream via-lav/40 to-white" aria-hidden="true" />
            <FloralCorner className="pointer-events-none absolute -right-8 bottom-8 h-48 w-48 -scale-x-100 text-lilac/40" />

            <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
                <div>
                    <ChapterHeading number="06" eyebrow="Contato" title={<>Vamos conversar sobre o <span className="italic text-serenity">cuidado</span> de quem você ama?</>} />
                    <FadeUp delay={0.15}>
                        <p className="mt-6 text-base leading-relaxed text-ink sm:text-lg">
                            Conte um pouco sobre quem você ama e o momento que sua família vive. Retornarei pessoalmente, com atenção e carinho. Atendimento domiciliar em Curitiba e região.
                        </p>
                    </FadeUp>
                    <div className="mt-9 space-y-5">
                        {[
                            { icon: Phone, label: "Telefone / WhatsApp", value: PHONE_DISPLAY, href: WHATSAPP_URL, testid: "contact-phone" },
                            { icon: Mail, label: "E-mail", value: EMAIL, href: `mailto:${EMAIL}`, testid: "contact-email" },
                            { icon: MapPin, label: "Região de atendimento", value: "Curitiba e região — Atendimento domiciliar", testid: "contact-location" },
                        ].map((c, i) => (
                            <FadeUp key={c.label} delay={0.2 + i * 0.1} className="flex items-center gap-4">
                                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-lilac to-serenity text-white shadow-md shadow-serenity/25">
                                    <c.icon size={20} />
                                </span>
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-serenity">{c.label}</p>
                                    {c.href ? (
                                        <a href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" data-testid={c.testid} className="text-base font-medium text-plum hover:text-serenity sm:text-lg">
                                            {c.value}
                                        </a>
                                    ) : (
                                        <p className="text-base font-medium text-plum sm:text-lg" data-testid={c.testid}>{c.value}</p>
                                    )}
                                </div>
                            </FadeUp>
                        ))}
                    </div>
                </div>

                <FadeUp delay={0.2}>
                    <form onSubmit={handleSubmit} className="rounded-[2rem] border-2 border-lilac bg-white p-7 shadow-2xl shadow-plum/10 sm:p-10" data-testid="contact-form">
                        <p className="font-serif text-2xl font-semibold text-plum">Envie uma mensagem</p>
                        <p className="mt-2 text-sm text-ink/70">Resposta direta pelo WhatsApp, sem compromisso.</p>

                        <div className="mt-7 space-y-5">
                            <div>
                                <label htmlFor="campo-nome" className="mb-2 block text-base font-bold text-plum">Nome</label>
                                <input id="campo-nome" required value={form.nome} onChange={set("nome")} placeholder="Seu nome completo" className={inputCls} data-testid="contact-name-input" />
                            </div>
                            <div>
                                <label htmlFor="campo-telefone" className="mb-2 block text-base font-bold text-plum">Telefone</label>
                                <input id="campo-telefone" required type="tel" value={form.telefone} onChange={set("telefone")} placeholder="(41) 90000-0000" className={inputCls} data-testid="contact-phone-input" />
                            </div>
                            <div>
                                <label htmlFor="campo-mensagem" className="mb-2 block text-base font-bold text-plum">Mensagem</label>
                                <textarea id="campo-mensagem" required rows={4} value={form.mensagem} onChange={set("mensagem")} placeholder="Conte um pouco sobre quem precisa de cuidado..." className={`${inputCls} resize-none`} data-testid="contact-message-input" />
                            </div>
                        </div>

                        <button
                            type="submit"
                            data-testid="contact-submit-button"
                            className="mt-8 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-gradient-to-r from-lilac via-serenity to-serenitylight px-8 py-4 text-base font-semibold text-white shadow-xl shadow-serenity/30 transition-transform duration-300 hover:scale-[1.03]"
                        >
                            <Send size={18} />
                            Enviar pelo WhatsApp
                        </button>
                    </form>
                </FadeUp>
            </div>
        </section>
    );
};

export default ContactSection;
